import { useCallback, useEffect, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { formatChf } from '@raporto/shared'
import { ActionButton } from '../ui/ActionButton.js'
import { notify, notifyError } from '../ui/Toasts.js'
import { api, type OffenerPunkt } from '../net/apiClient.js'

/**
 * Was ist offen.
 *
 * Bewusst kein Dashboard. Ein Dashboard beantwortet «wie läuft es» – eine
 * Frage, die ein Betrieb mit sechs Leuten am Dienstagmorgen nicht hat. Die
 * Frage lautet: **was muss ich heute anfassen.**
 *
 * Deshalb ist jeder Eintrag eine Aufgabe mit genau einer Handlung und keine
 * Kennzahl, und die Reihenfolge ist die der Dringlichkeit: was kaputt ist, steht
 * oben, weil es sonst zwischen dreissig offenen Rechnungen untergeht.
 *
 * Die einzige Zahl ganz oben ist der offene Betrag. Sie steht dort, weil sie die
 * eine ist, nach der jemand tatsächlich fragt.
 */

const ART_TEXT: Record<string, string> = {
  versand_unklar: 'Versand unklar',
  versand_fehlgeschlagen: 'Versand fehlgeschlagen',
  pdf_fehlt: 'PDF fehlt',
  ueberfaellig: 'überfällig',
  ueberbezahlt: 'überbezahlt',
  nicht_gesendet: 'nicht gesendet',
  teilbezahlt: 'teilbezahlt',
  unbezahlt: 'unbezahlt',
  entwurf: 'Entwurf',
  rapporte_offen: 'Rapporte offen'
}

export function OpenWork({
  session,
  onOeffnen
}: {
  session: SessionUser
  /** Springt in den passenden Bereich – die Liste ist ein Sprungbrett, kein Ziel. */
  onOeffnen?: (ziel: { typ: 'beleg' | 'rapporte'; id: string }) => void
}): JSX.Element {
  const [items, setItems] = useState<OffenerPunkt[]>([])
  const [summe, setSumme] = useState({ gesamt: 0, probleme: 0, ueberfaellig: 0, offenRp: 0 })
  const [loading, setLoading] = useState(true)
  const [exportOffen, setExportOffen] = useState(false)

  const laden = useCallback(async (): Promise<void> => {
    const r = await api.openWork()
    setItems(r.items)
    setSumme(r.zusammenfassung)
    setLoading(false)
  }, [])

  useEffect(() => {
    void laden().catch(() => setLoading(false))
  }, [laden])

  if (session.role !== 'superuser') {
    return (
      <div className="card">
        <p className="muted">Diese Liste ist für das Büro.</p>
      </div>
    )
  }

  return (
    <>
      <div className="card">
        <div className="listhead">
          <h2>Was ist offen</h2>
          <span className="muted">{summe.gesamt}</span>
        </div>

        {summe.gesamt === 0 ? (
          <p className="muted" id="nichts-offen">
            Nichts offen. Alles verrechnet, versendet und bezahlt.
          </p>
        ) : (
          <p className="muted small">
            {summe.offenRp > 0 && (
              <>
                Offene Forderungen: <strong>CHF {formatChf(summe.offenRp)}</strong>.{' '}
              </>
            )}
            {summe.probleme > 0 && `${summe.probleme} brauchen zuerst Aufmerksamkeit. `}
            {summe.ueberfaellig > 0 && `${summe.ueberfaellig} überfällig.`}
          </p>
        )}

        {loading ? (
          <p className="muted">Wird geladen …</p>
        ) : (
          <ul className="records" id="offene-punkte">
            {items.map((p) => (
              <li key={`${p.art}-${p.id}`}>
                <button
                  type="button"
                  id={`offen-${p.id}`}
                  className={`aufgabe stufe-${p.dringlichkeit}`}
                  onClick={() => onOeffnen?.(p.ziel)}
                >
                  <span className="rec-name">
                    {p.titel}
                    <span
                      className={`pill small ${
                        p.dringlichkeit === 1 ? 'crit' : p.dringlichkeit === 2 ? 'warn' : ''
                      }`}
                    >
                      {ART_TEXT[p.art] ?? p.art}
                    </span>
                  </span>
                  <span className="rec-sub">{p.text}</span>
                  <span className="aufgabe-fuss">
                    {p.offenRp !== undefined && p.offenRp > 0 && (
                      <span className="betrag">CHF {formatChf(p.offenRp)}</span>
                    )}
                    <span className="tuwas">{p.aktion} →</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <ActionButton
          id="btn-offen-neuladen"
          variant="secondary"
          labels={{ idle: 'Neu laden', busy: 'Lädt …', success: 'Aktuell' }}
          onAction={laden}
          onError={(err) => notifyError(err)}
        />
      </div>

      <ExportKarte offen={exportOffen} setOffen={setExportOffen} />
    </>
  )
}

// ---------------------------------------------------------------------------

/**
 * Export.
 *
 * Zuerst die Vorschau, dann die Datei. Ein Export kann gross werden, und – was
 * wichtiger ist – er kann unvollständig sein, wenn Rechnungen ohne PDF
 * herumliegen. Wer das vorher sieht, erzeugt erst das PDF und dann den Export.
 *
 * Heruntergeladen wird über einen echten Link und nicht über JavaScript: der
 * Browser kann das besser, und ein Download, der im Browser zusammengebaut wird,
 * hängt bei hundert Megabyte im Arbeitsspeicher.
 */
function ExportKarte({
  offen,
  setOffen
}: {
  offen: boolean
  setOffen: (o: boolean) => void
}): JSX.Element {
  const [vorschau, setVorschau] = useState<Awaited<
    ReturnType<typeof api.exportPreview>
  > | null>(null)

  return (
    <div className="card">
      <h2>Export</h2>
      <p className="muted small">
        Alles für die Buchhaltung als ZIP: Rechnungen, Zahlungen, Kunden als CSV, dazu die
        Rechnungs-PDFs und ein Prüfprotokoll. Fotos und Zugangsdaten sind nicht enthalten.
      </p>

      {!offen ? (
        <ActionButton
          id="btn-export-vorschau"
          variant="secondary"
          labels={{ idle: 'Export vorbereiten', busy: 'Zählt …', success: 'Bereit' }}
          onAction={async () => {
            setVorschau(await api.exportPreview())
            setOffen(true)
          }}
          onError={(err) => notifyError(err)}
        />
      ) : (
        <>
          {vorschau && (
            <>
              <dl className="counts" id="export-vorschau">
                <dt>Rechnungen</dt>
                <dd>{vorschau.rechnungen}</dd>
                <dt>davon mit PDF</dt>
                <dd>{vorschau.pdfs}</dd>
                <dt>Zahlungen</dt>
                <dd>{vorschau.zahlungen}</dd>
                <dt>Kunden</dt>
                <dd>{vorschau.kunden}</dd>
              </dl>

              {vorschau.ohnePdf.length > 0 && (
                <div className="problemcard" id="export-warnung">
                  <p>
                    <strong>
                      {vorschau.ohnePdf.length} Rechnung
                      {vorschau.ohnePdf.length > 1 ? 'en haben' : ' hat'} kein PDF.
                    </strong>{' '}
                    Sie stehen mit Betrag in der CSV, aber ohne Datei. Das Prüfprotokoll nennt sie
                    ausdrücklich – verschwiegen wird nichts.
                  </p>
                  <p className="muted small">
                    {vorschau.ohnePdf.map((f) => f.number).join(', ')}
                  </p>
                </div>
              )}
            </>
          )}

          <div className="rowactions">
            <a
              className="linkbutton"
              id="btn-export-laden"
              href="/exports/zip"
              onClick={() => notify.info('Der Export wird erzeugt. Das kann einen Moment dauern.')}
            >
              ZIP herunterladen
            </a>
            <ActionButton
              variant="quiet"
              labels={{ idle: 'Schliessen', busy: '…' }}
              onAction={async () => setOffen(false)}
            />
          </div>
        </>
      )}
    </div>
  )
}
